'use client';

import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The band across the top of a Collection or care page, after the
 * event-details hero: the art blown up and blurred behind, the poster in
 * front, the title and the facts beside it under condensed labels.
 * OpportunityPage draws the same thing for one opportunity.
 */
export const KICKER =
  'font-[family-name:var(--font-display)] text-[15px] leading-none tracking-[0.06em] uppercase';

export interface BandFact {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}

export function PageBand({
  art,
  backHref,
  backLabel,
  title,
  facts,
}: {
  /** The poster, already sized to fill its box */
  art: ReactNode;
  backHref?: string;
  backLabel?: string;
  title: string;
  facts: BandFact[];
}) {
  return (
    <section className="bleed relative overflow-hidden bg-[color-mix(in_srgb,var(--accent,#1c2b39)_45%,#1c2b39)] text-white">
      <div className="pointer-events-none absolute inset-0 scale-125 blur-3xl" aria-hidden="true">
        {art}
      </div>
      <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
      <div className="bleed-inner bleed-headroom relative flex flex-col gap-6 pb-7 sm:pb-9">
        {backHref && (
          <a
            href={backHref}
            className={`${KICKER} inline-flex w-fit items-center gap-1 text-white/85 no-underline transition-opacity hover:text-white`}
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={3} />
            {backLabel}
          </a>
        )}
        <div className="grid items-start gap-6 md:grid-cols-[minmax(0,364px)_1fr] md:gap-8">
          <div className="aspect-video w-full overflow-hidden rounded-2xl shadow-[0_18px_40px_rgba(0,0,0,.35)] ring-1 ring-white/20">
            {art}
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            <h1 className="text-2xl leading-tight font-extrabold tracking-tight uppercase sm:text-3xl">
              {title}
            </h1>
            {facts.map((f) => (
              <div key={f.label} className="grid grid-cols-[22px_1fr] gap-x-3 gap-y-1.5">
                <span className="mt-px text-white/80">{f.icon}</span>
                <span className={`${KICKER} text-white/75`}>{f.label}</span>
                <div className="col-start-2 text-[15px] font-semibold text-white">
                  {f.value}
                  {f.sub && (
                    <span className="block text-sm font-medium text-white/75">{f.sub}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** The art for a band: an image, else the navy gradient every finder card uses. */
export function BandArt({ imageUrl }: { imageUrl: string | null | undefined }) {
  return imageUrl ? (
    <img src={imageUrl} alt="" className="h-full w-full object-cover" draggable={false} />
  ) : (
    <div
      className="h-full w-full"
      style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)' }}
    />
  );
}

/** Accent custom properties for an element, by the event rules (lib/accent.ts). */
export { accentTokens, isHexColor } from '@/lib/accent';
